import React, { useState, useEffect, useCallback } from 'react';
import { Fab, Tooltip } from "@mui/material";
import Comment from '@mui/icons-material/Comment';

import './Chat.css';

import { Campaign, Conversation, Receivable, useCampaignsReceivableAlerts, useUserReceivableAlerts } from '@dndModels';
import { DisablableOption } from '@models';

// import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDAPI from '@dndUtilities/system_api';
import FlexBox from '@components/layout/FlexBox';
import SelectStringBox from '@components/input/selects/SelectStringBox';
import ChatBox from './ChatBox';
import ToastBox from '@components/display/ToastBox';

interface Props {
}

const api = DandDAPI.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ChatsButton({ 
}: Props) {
  if (api.user) {
    // Get messages directly to this user.
    useUserReceivableAlerts(api.user._id, async (receivable) => {
      // swap this for your actual toast call
      // showToast(`${receivable.from_id} sent: ${receivable.message}`);
      if (receivable._id && !received_ids.includes(receivable._id)) {
        setReceivedIds([...received_ids, receivable._id]);
        // console.log(receivable);
        const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
        // console.log(processed_group);
        if (processed_group.length === 1) {
          const processed = processed_group[0];
          // See if there's already a convo for it.
          let convo = conversations.find(o => o.id === processed.from_id);
          if (convo) {
            if (processed.temp) {
              // It's probably deleting the conversation.
              if (processed.message === "Conversation Deleted") {
                setConversations(conversations.filter(o => o.id !== processed.from_id));
              }
            } else {
              convo.messages.push(processed);
              if (!convo.open) {
                convo.flashing = true;
                setToastTime(common_util.get_time_number());
                setToastMsg(`${convo.name} says: ${processed.message}`);
              }
            }
          } else if (!processed.temp) {
            // Ignore it if it's temp. We already don't have it in memory, so we don't need it right now.
            const user = api.my_users.find(o => o._id === processed.from_id);
            if (user) {
              convo = new Conversation();
              convo.type = "User";
              convo.id = processed.from_id;
              convo.name = user.name;
              convo.flashing = true;
              await api.loadConversation(convo);
              setConversations([...conversations, convo]);
              setToastTime(common_util.get_time_number());
              setToastMsg(`${convo.name} says: ${processed.message}`);
            }
          }
          setReloading(true);
          ping();
        }
      }
    });
    // Get campaign messages.
    // debugger;

    useCampaignsReceivableAlerts(api.user.campaign_ids, async (campaign_id, receivable) => {
      // swap this for your actual toast call
      // showToast(`${receivable.from_id} sent: ${receivable.message}`);
      if (receivable._id && !received_ids.includes(receivable._id)) {
        setReceivedIds([...received_ids, receivable._id]);
        console.log(receivable);
        const processed_group = await api.processResponse2<Receivable>({ all: [receivable], data_type: "receivable" });
        // console.log(processed_group);
        if (processed_group.length === 1) {
          const processed = processed_group[0];
          if (processed.type === "Chat") {
            const from = api.my_users.find(o => o._id === processed.from_id);
            if (from) {
              // See if there's already a convo for it.
              let convo = conversations.find(o => o.id === processed.campaign_id);
              if (convo) {
                if (processed.temp) {
                  // It's probably deleting the conversation.
                  if (processed.message === "Conversation Deleted") {
                    setConversations(conversations.filter(o => o.id !== processed.campaign_id));
                  }
                } else {
                  convo.messages.push(processed);
                  if (!convo.open) {
                    convo.flashing = true;
                    setToastTime(common_util.get_time_number());
                    setToastMsg(`In ${convo.name} Campaign ${from.name} says: ${processed.message}`);
                  }
                }
              } else if (!processed.temp) {
                // Ignore it if it's temp. We already don't have it in memory, so we don't need it right now.
                const campaign = api.my_campaigns.find(o => o._id === processed.campaign_id);
                if (campaign) {
                  convo = new Conversation();
                  convo.type = "Campaign";
                  convo.id = processed.campaign_id;
                  convo.name = campaign.name;
                  convo.flashing = true;
                  await api.loadConversation(convo);
                  setConversations([...conversations, convo]);
                  setToastTime(common_util.get_time_number());
                  setToastMsg(`In ${convo.name} Campaign ${from.name} says: ${processed.message}`);
                }
              }
            }
            setReloading(true);
            ping();
          }
        }
      }
    });
  }
  const [received_ids, setReceivedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [campaign_id, setCampaignId] = useState("");
  const [convo_type, setConvoType] = useState("User");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [processing, setProcessing] = useState(false);
  const [ping_time, setPingTime] = useState(0);
  const [deleting_convo, setDeletingConvo] = useState<Conversation | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const [toast_msg, setToastMsg] = useState("");

  const ping = () => {
    setPingTime(common_util.get_time_number());
  }

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const deleteConvo = useCallback( async (convo: Conversation) => {
    await api.deleteObjects("receivable", convo.messages);
    const receivable = new Receivable();
    receivable.from_id = api.user_id;
    if (convo.type === "User") {
      receivable.user_id = convo.id;
    } else {
      receivable.campaign_id = convo.id;
    }
    receivable.sent_dt = common_util.get_time_number();
    receivable.message = "Conversation Deleted";
    receivable.origin_id = api.temp_id;
    receivable.type = "Chat";
    receivable.temp = true;
    await api.upsertObject("receivable", receivable);
    setDeletingConvo(undefined);
    setDeleting(false);
  }, [api]);

  useEffect(() => {
    if (deleting_convo && !deleting) {
      setDeleting(true);
      setTimeout(() => {
        deleteConvo(deleting_convo);
      }, 100);
    }
  }, [deleting_convo, deleting]);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        '&:not(.disabled)': {
          backgroundColor: theme.palette.secondary.main,
        },
        '&.disabled': {
          backgroundColor: "gray",
          color: "black"
        }
      },
    });
  });

  const { classes, cx } = useStyles();

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
          }}>
          { renderAvailableChats() }
        </ModalOrDrawer>
        <ToastBox
          openTime={ toast_time }
        >
          { toast_msg }
        </ToastBox>
      </>
    );
  }

  const renderAvailableChats = () => {
    if (api.user) {
      const my_campaigns = api.user.my_campaigns.filter(o => !o.archived);
      const filtered_users = campaign_id === "" ? api.my_users : api.my_users.filter(o => o.campaign_ids.includes(campaign_id));
      return (
        <FlexBox direction="column">
          <FlexBox>
            <SelectStringBox 
              name="Type"
              options={["User", "Campaign"]}
              value={convo_type}
              onChange={setConvoType}
            />
          </FlexBox>
          { my_campaigns.length > 0 && renderCampaigns(my_campaigns) }
          { convo_type === "User" && filtered_users.map(user => {
            return (
              <FlexBox key={user._id} className="padded">
                <FlexBox className={cx(classes.root, `padded clickable ${ processing ? "disabled" : ""}`)} onClick={ async () => {
                  if (api.user) {
                    let convo = conversations.find(o => o.id === user._id);
                    if (convo) {
                      convo.open = true;
                    } else {
                      setProcessing(true);
                      convo = new Conversation();
                      convo.type = "User";
                      convo.id = user._id;
                      convo.name = user.name;
                      convo.open = true;
                      // debugger;
                      await api.loadConversation(convo);
                      setConversations([...conversations, convo]);
                      setProcessing(false);
                    }
                    setOpen(false);
                    ping();
                  }
                }}>
                  { user.name }
                </FlexBox>
              </FlexBox>
            );
          })}
        </FlexBox>
      );
    }
    return <></>;
  }

  const renderCampaigns = (my_campaigns: Campaign[]) => {
    if (convo_type === "User") {
      const options = my_campaigns.map(o => {
        const opt: DisablableOption = {
          id: o._id, text: o.name
        };
        return opt;
      });
      return (
        <FlexBox>
          <SelectStringBox
            name="Filter by Campaign"
            options={options}
            value={campaign_id}
            onChange={setCampaignId}
          />
        </FlexBox>  
      );
    } else {
      return my_campaigns.map(campaign => {
        return (
          <FlexBox key={campaign._id} className="padded">
            <FlexBox className={cx(classes.root, `padded clickable ${ processing ? "disabled" : ""}`)} onClick={ async () => {
              if (api.user) {
                let convo = conversations.find(o => o.id === campaign._id);
                if (convo) {
                  convo.open = true;
                } else {
                  setProcessing(true);
                  const convo = new Conversation();
                  convo.type = "Campaign";
                  convo.id = campaign._id;
                  convo.name = campaign.name;
                  convo.open = true;
                  await api.loadConversation(convo);
                  setConversations([...conversations, convo]);
                  setProcessing(false);
                }
                setOpen(false);
                ping();
              }
            }}>
              { campaign.name }
            </FlexBox>
          </FlexBox>
        );
      });
    }
  }

  const renderChatButton = () => {
    if (api.user) {
      return (
        <Tooltip title={`Chat`}>
          <Fab size="small" color="primary" style={{marginLeft: "8px"}}
            onClick={ () => {
              setOpen(true);
            }}>
            <Comment />
          </Fab>
        </Tooltip>
      ); 
    }
    return null;
  }

  const renderConversations = () => {
    return conversations.map(convo => renderConversation(convo));
  }

  const renderConversation = (convo: Conversation) => {
    return (
      <FlexBox key={convo.id}>
        <ChatBox 
          convo={convo} 
          ping_time={ping_time} 
          onDelete={ async () => {
            setDeletingConvo(convo);
            setConversations(conversations.filter(o => o.id !== convo.id));
            setReloading(true);
          }}
        />
      </FlexBox>
    );
  }

  const renderChatsBar = () => {
    if (api.user) {
      return (
        <div className="bottom-bar">
          <div className="bottom-bar-content">
            <FlexBox direction="row" style={{ float: "right" }}>
              { renderConversations() }
              { renderChatButton() }
            </FlexBox>
          </div>
        </div>
      ); 
    }
    return null;
  }

  return (
    <>
      { renderChatsBar() }
      { renderExtras() }
    </>
  );
}
